/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { keysCreate } from "../funcs/keysCreate.js";
import { keysExtendAgentKeys } from "../funcs/keysExtendAgentKeys.js";
import { keysList } from "../funcs/keysList.js";
import { keysRevokeById } from "../funcs/keysRevokeById.js";
import { keysRotate } from "../funcs/keysRotate.js";
import { keysUpdatePolicy } from "../funcs/keysUpdatePolicy.js";
import { keysValidate } from "../funcs/keysValidate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { Key } from "../models/components/key.js";
import { ListKeysResult } from "../models/components/listkeysresult.js";
import { ValidateKeyResult } from "../models/components/validatekeyresult.js";
import {
  CreateAPIKeyRequest,
  CreateAPIKeySecurity,
} from "../models/operations/createapikey.js";
import {
  ExtendAgentAPIKeysRequest,
  ExtendAgentAPIKeysSecurity,
} from "../models/operations/extendagentapikeys.js";
import {
  ListAPIKeysRequest,
  ListAPIKeysSecurity,
} from "../models/operations/listapikeys.js";
import {
  RevokeAPIKeyRequest,
  RevokeAPIKeySecurity,
} from "../models/operations/revokeapikey.js";
import {
  RotateAPIKeyRequest,
  RotateAPIKeySecurity,
} from "../models/operations/rotateapikey.js";
import {
  UpdateAPIKeyPolicyRequest,
  UpdateAPIKeyPolicySecurity,
} from "../models/operations/updateapikeypolicy.js";
import {
  ValidateAPIKeyRequest,
  ValidateAPIKeySecurity,
} from "../models/operations/validateapikey.js";
import { unwrapAsync } from "../types/fp.js";

export class Keys extends ClientSDK {
  /**
   * createKey keys
   *
   * @remarks
   * Create a new api key
   */
  async create(
    request: CreateAPIKeyRequest,
    security?: CreateAPIKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<Key> {
    return unwrapAsync(keysCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * extendAgentKeys keys
   *
   * @remarks
   * Add mcp:connect grants to every live key the caller issued for an agent, so servers the agent is given reach the keys it already runs with. Each key's new policy is checked like issuance: the agent, its owner and the caller must all allow it. Keys issued by someone else are left as they are. The result lists only the keys this call changed: keys that already held the grants, expired keys and other people's keys are not in it.
   */
  async extendAgentKeys(
    request: ExtendAgentAPIKeysRequest,
    security?: ExtendAgentAPIKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListKeysResult> {
    return unwrapAsync(keysExtendAgentKeys(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listKeys keys
   *
   * @remarks
   * List all api keys for an organization
   */
  async list(
    request?: ListAPIKeysRequest | undefined,
    security?: ListAPIKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListKeysResult> {
    return unwrapAsync(keysList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * revokeKey keys
   *
   * @remarks
   * Revoke a api key
   */
  async revokeById(
    request: RevokeAPIKeyRequest,
    security?: RevokeAPIKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(keysRevokeById(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * rotateKey keys
   *
   * @remarks
   * Rotate an API key. Agent-key rotation replaces immutable delegation and directly revokes the old row.
   */
  async rotate(
    request: RotateAPIKeyRequest,
    security?: RotateAPIKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<Key> {
    return unwrapAsync(keysRotate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateKeyPolicy keys
   *
   * @remarks
   * Replace an ordinary key's own policy: the mcp:connect and mcp:auth grants, and their blocks, its server and auth client access is evaluated against. Agent keys keep their delegated grants. Requires org admin.
   */
  async updatePolicy(
    request: UpdateAPIKeyPolicyRequest,
    security?: UpdateAPIKeyPolicySecurity | undefined,
    options?: RequestOptions,
  ): Promise<Key> {
    return unwrapAsync(keysUpdatePolicy(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * verifyKey keys
   *
   * @remarks
   * Verify an api key
   */
  async validate(
    request?: ValidateAPIKeyRequest | undefined,
    security?: ValidateAPIKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<ValidateKeyResult> {
    return unwrapAsync(keysValidate(
      this,
      request,
      security,
      options,
    ));
  }
}
